/**
 * React Query hooks for sponsor funding awards & remittances (mig 163).
 * Mirrors the zakat hooks in useDiscounts.ts; all data under the
 * ["sponsor"] key family so mutations can invalidate the whole surface
 * (awards, summary, open invoices, remittances) at once.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { discountsApi } from "@/api/discounts";
import { getApiErrorMessage } from "@/utils/errors";
import { useCurrentTenant } from "@/hooks/useTenants";
import type {
  GrantFundingAwardRequest,
  RecordRemittanceRequest,
  RevokeFundingAwardRequest,
  UpdateFundingAwardRequest,
} from "@/types/funding";

/**
 * Tenant gate for ALL admin sponsor-funding surfaces. Reads the current
 * tenant's settings.sponsor_funding (mig 163) — absent or disabled means
 * every sponsor UI element (sidebar entry, student awards card, TEFA
 * approval fields, invoice funding filter) stays hidden. Display-only:
 * the backend re-validates on every write.
 */
export const useSponsorFundingConfig = () => {
  const { data: tenant } = useCurrentTenant();
  const config = tenant?.settings?.sponsor_funding ?? null;
  return {
    enabled: !!config?.enabled,
    sponsorName: config?.sponsor_name || "Sponsor",
    programName: config?.program_name || "Sponsor",
    annualRate: config?.annual_rate != null ? String(config.annual_rate) : null,
  };
};

export const useFundingAwards = (params?: {
  status?: string;
  period_label?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ["sponsor", "awards", params],
    queryFn: async () => {
      const response = await discountsApi.listFundingAwards(params);
      return response.data;
    },
  });
};

export const useStudentFundingAwards = (studentId: string, enabled = true) => {
  return useQuery({
    queryKey: ["sponsor", "student-awards", studentId],
    queryFn: async () => {
      const response = await discountsApi.getStudentFundingAwards(studentId);
      return response.data;
    },
    enabled: enabled && !!studentId,
  });
};

export const useGrantFundingAward = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: GrantFundingAwardRequest) => {
      const response = await discountsApi.grantFundingAward(data);
      return response.data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["sponsor"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      if (result.invoice) {
        toast.success(
          `Award granted — sponsor invoice ${result.invoice.invoice_number ?? ""} created`,
        );
      } else {
        // The award stands; the invoice can be healed via regenerate.
        toast.error(
          "Award granted but the sponsor invoice failed to generate — use Retry Invoice on the award",
        );
      }
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Failed to grant funding award"));
    },
  });
};

export const useUpdateFundingAward = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      awardId,
      data,
    }: {
      awardId: string;
      data: UpdateFundingAwardRequest;
    }) => {
      const response = await discountsApi.updateFundingAward(awardId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sponsor"] });
      toast.success("Funding award updated");
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Failed to update funding award"));
    },
  });
};

export const useRevokeFundingAward = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      awardId,
      data,
    }: {
      awardId: string;
      data: RevokeFundingAwardRequest;
    }) => {
      const response = await discountsApi.revokeFundingAward(awardId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sponsor"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      toast.success(
        "Award revoked — family billing resumes after the shortened period; credit-memo the sponsor invoice for the unearned remainder",
      );
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Failed to revoke funding award"));
    },
  });
};

export const useRegenerateFundingInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (awardId: string) => {
      const response = await discountsApi.regenerateFundingInvoice(awardId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sponsor"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      toast.success("Sponsor invoice generated");
    },
    onError: (error: unknown) => {
      toast.error(
        getApiErrorMessage(error, "Failed to generate the sponsor invoice"),
      );
    },
  });
};

export const useSponsorSummary = (enabled = true) => {
  return useQuery({
    queryKey: ["sponsor", "summary"],
    queryFn: async () => {
      const response = await discountsApi.getSponsorSummary();
      return response.data;
    },
    enabled,
  });
};

export const useOpenSponsorInvoices = (enabled = true) => {
  return useQuery({
    queryKey: ["sponsor", "open-invoices"],
    queryFn: async () => {
      const response = await discountsApi.listOpenSponsorInvoices();
      return response.data;
    },
    enabled,
  });
};

export const useSponsorRemittances = (
  params?: {
    page?: number;
    limit?: number;
  },
  enabled = true,
) => {
  return useQuery({
    queryKey: ["sponsor", "remittances", params],
    queryFn: async () => {
      const response = await discountsApi.listSponsorRemittances(params);
      return response.data;
    },
    enabled,
  });
};

export const useSponsorRemittanceDetails = (remittanceId: string) => {
  return useQuery({
    queryKey: ["sponsor", "remittance", remittanceId],
    queryFn: async () => {
      const response = await discountsApi.getSponsorRemittance(remittanceId);
      return response.data;
    },
    enabled: !!remittanceId,
  });
};

export const useRecordSponsorRemittance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: RecordRemittanceRequest) => {
      const response = await discountsApi.recordSponsorRemittance(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sponsor"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      toast.success("Remittance recorded — sponsor invoices settled");
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Failed to record remittance"));
    },
  });
};
